import { Autocomplete, Box, Button, Chip, Divider, Paper, TextField, Typography } from "@mui/material";
import Dropdown, { DropdownOption } from "../components/ui/dropdown";
import React from "react";
import { Sparkles } from "lucide-react";

import Topbar from "../components/topbar";
import ModalWrapper from "../components/modal";
import UnitSelect from "../components/ui/unit-select";
import Toggle from "../components/ui/toggle";
import { useToast } from "../providers/toaster-provider";
import { useUser } from "../providers/user-provider";
import { usePermissions } from "../providers/permissions-provider";
import { useFeatures } from "../providers/features-provider";
import currencies from '../files/json/currencies.json';
import usStates from '../files/json/us-states.json';
import swissCantons from '../files/json/switzerland-cantons.json';
import timezones from '../files/json/timezones.json';

import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';
import SectionWrapper from "../components/section";
import SubscriptionPlans, { AddonsCheckout, OverduePaymentCheckout, OverduePayment } from "../components/popup/subscription-plans";
import { useSubscriptionTiers, addonDescription, parseCapacityKey } from "../util/subscription-tiers";
import { formatDate } from "../util/date";

type SubscriptionStatusResponse = {
    tierId: string;
    status: 'active' | 'expired';
    startedAt: string;
    renewsOn: string | null;
    autoRenew: boolean;
    trialEndsOn: string | null;
    pendingNotification: { fromTierId: string; toTierId: string; reason: string; at: string } | null;
    // express-server subscription-addons-service.js's getAddonsState - one
    // per paid add-on the company's plan offers.
    addons?: {
        key: string;
        label: string;
        price: number | null;
        chargePerPeriod: number;
        billing: 'monthly' | 'annual' | 'daily' | null;
        enabled: boolean;
        endsAt: string | null;
        hasAccess: boolean;
        billedFrom: string | null;
    }[];
};

type BillingHistoryRow = {
    guid: string;
    description: string;
    amount: number;
    date: string;
    type: 'payment' | 'refund';
    invoiceNumber: string | null;
};

// Maps a TierConfig boolean field name (the `reason` on an auto-upgrade
// notification - see subscription-guard-service.js's requireFeature) to a
// human label for display.
const REASON_LABELS: Record<string, string> = {
    xeroIntegration: 'Xero integration',
    stripeIntegration: 'Stripe payments',
    userAcl: 'user roles & permissions',
};

const formatMoney = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const formatMoneyCents = (n: number) => `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box>
        <Typography fontWeight={500} fontSize={14} mb={0.5} color="var(--color-text-muted)">{label}</Typography>
        <Typography fontSize={15}>{value ?? '—'}</Typography>
    </Box>
);

// 180 and 365 keep their historical friendly labels (and VALUES - matching
// exactly what used to be hardcoded here, so an entity with one of these
// already saved still shows as selected); anything else falls back to a
// plain day count. Everything else about this list is now derived live
// from the tiers themselves (see projectionOptions below) rather than
// hardcoded - a tier's horizon changing, or a new tier being added, used to
// require remembering to update this list by hand too, which is exactly
// how "180 days" went missing and "2 years" lingered as an option no plan
// could actually satisfy.
const daysToHorizonLabel = (days: number): string => {
    if (days === 180) return '6 months';
    if (days === 365) return '1 year';
    if (days > 365 && days % 365 === 0) return `${days / 365} years`;
    return `${days} days`;
};

const dateFormats = [
    { value: 'DD MMM YYYY', label: 'DD MMM YYYY' },
    { value: 'MMM DD, YYYY', label: 'MMM DD, YYYY' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
    { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY' },
];

const AccountSettings: React.FC = () => {
    const { user } = useUser();
    const { can, isAdmin } = usePermissions();
    const canUpdate = can('settings_company', 'update');
    const { showSuccess, showError } = useToast();
    const { reload: reloadFeatures } = useFeatures();

    // States
    const [openModal, setOpenModal] = React.useState<boolean>(false);

    // Form fields
    const [countryOptions, setCountryOptions] = React.useState<DropdownOption[]>([]);
    const [countries, setCountries] = React.useState<{ id: number; country: string; currency: string; dateformat: string; timezone: string }[]>([]);
    const [selectedCountry, setSelectedCountry] = React.useState<string>("");
    const [companyName, setCompanyName] = React.useState<string>("");
    const [incorporationNumber, setIncorporationNumber] = React.useState<string>("");
    const [taxNumbers, setTaxNumbers] = React.useState<string>("");
    const [projection, setProjection] = React.useState<string>("1 year");
    const [startDayOfWeek, setStartDayOfWeek] = React.useState<string>("");
    const [fiscalYearStartMonth, setFiscalYearStartMonth] = React.useState<string>("7");
    const [decryptionCode, setDecryptionCode] = React.useState<string>("");
    const [visibleHistory, setVisibleHistory] = React.useState<string>("");
    const [selectedState, setSelectedState] = React.useState<string | null>(null);
    const [nextInvoiceNumber, setNextInvoiceNumber] = React.useState<string>('0000001');
    const [consolidateOneTimeThreshold, setConsolidateOneTimeThreshold] = React.useState<string>('');
    const [status, setStatus] = React.useState<string>('invoice');
    const [errors, setErrors] = React.useState<{ companyName?: string; selectedCountry?: string; timeZone?: string; selectedState?: string }>({});

	const currencyOptions = React.useMemo(() => 
        currencies.map(cur => ({ label: `${cur.name} (${cur.symbol})`, value: cur.name })), 
        []
    );

    // These fields are fetched but not editable
    const [dateFormat, setDateFormat] = React.useState<string>("YYYY-MM-DD");
    const [currency, setCurrency] = React.useState<{ label: string; value: string } | null>(null);
    const [timeZone, setTimeZone] = React.useState<string>("");

    // Original values for change detection - per section
    const [originalCompanyDetails, setOriginalCompanyDetails] = React.useState<{
        companyName: string;
        incorporationNumber: string;
        taxNumbers: string;
    } | null>(null);

    const [originalLocalisation, setOriginalLocalisation] = React.useState<{
        selectedCountry: string;
        selectedState: string | null;
        timeZone: string;
        dateFormat: string;
        currency: { label: string; value: string } | null;
        startDayOfWeek: string;
        fiscalYearStartMonth: string;
    } | null>(null);

    const [originalSecurity, setOriginalSecurity] = React.useState<{
        decryptionCode: string;
    } | null>(null);

    const [originalInternalDocuments, setOriginalInternalDocuments] = React.useState<{
        nextInvoiceNumber: string;
    } | null>(null);

    const [originalForecasting, setOriginalForecasting] = React.useState<{
        projection: string;
        visibleHistory: string;
        consolidateOneTimeThreshold: string;
    } | null>(null);

    const [fetchCompany, setFetchCompany] = useApi();
    const [fetchCountries, setFetchCountries] = useApi();
    const [updateCompany, setUpdateCompany] = useApi();
    const { tiers } = useSubscriptionTiers();

    // Subscription section state (moved here from profile-settings.tsx -
    // billing is a company-level, admin-managed concern, not a personal
    // profile field).
    const subscriptionSectionRef = React.useRef<HTMLDivElement | null>(null);
    const [showPlans, setShowPlans] = React.useState(false);
    const [notificationDismissed, setNotificationDismissed] = React.useState(false);
    const [fetchSubscription, setFetchSubscription] = useApi<SubscriptionStatusResponse>();
    const [fetchBillingHistory, setFetchBillingHistory] = useApi<{ data: BillingHistoryRow[] }>();
    const [, setAcknowledgeUpgrade] = useApi<{ success: boolean }>();
    const [, setToggleAutoRenew] = useApi<{ success: boolean }>();
    const [autoRenewOverride, setAutoRenewOverride] = React.useState<boolean | null>(null);

    // A subscription payment that didn't go through - paid from here.
    const [fetchOverdue, setFetchOverdue] = useApi<OverduePayment & { success: boolean }>();
    const overdue = fetchOverdue.data?.overdue ? fetchOverdue.data : null;
    const [showOverduePay, setShowOverduePay] = React.useState(false);

    const refreshSubscription = React.useCallback(() => {
        if (user?.entityId) {
            setFetchSubscription(buildApiRequestConfig(routes.subscription.fetchStatus, [
                { param: 'entityId', value: user.entityId }
            ]));
            setFetchBillingHistory(buildApiRequestConfig(routes.subscription.billingHistory, [
                { param: 'entityId', value: user.entityId }
            ]));
            setFetchOverdue(buildApiRequestConfig(routes.subscription.overdue, [
                { param: 'entityId', value: user.entityId }
            ]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.entityId]);

    React.useEffect(() => {
        refreshSubscription();
    }, [refreshSubscription]);

    // Deep-link from the account menu's "Billing & Subscription" item
    // (user-actions.tsx) - lands on this page and scrolls straight to the
    // section instead of making an admin hunt for it among the others.
    React.useEffect(() => {
        if (window.location.hash === '#subscription' && subscriptionSectionRef.current) {
            subscriptionSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, []);

    const subscription = fetchSubscription.data;
    const currentTier = tiers.find(t => t.id === subscription?.tierId);
    const isExpired = subscription?.status === 'expired';
    // Already on the top plan (longest horizon) = nothing to upgrade to.
    const notification = !notificationDismissed ? subscription?.pendingNotification : null;
    const upgradedToTier = notification ? tiers.find(t => t.id === notification.toTierId) : null;
    const autoRenew = autoRenewOverride ?? subscription?.autoRenew ?? true;
    const billingHistory = fetchBillingHistory.data?.data ?? [];

    const handleToggleAutoRenew = (checked: boolean) => {
        setAutoRenewOverride(checked);
        if (!user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.subscription.toggleAutoRenew, [
            { param: 'entityId', value: user.entityId }
        ]);
        setToggleAutoRenew({ url, method, body: { autoRenew: checked } });
    };

    // Add-ons - changed on the add-ons checkout (AddonsCheckout), which
    // refreshes the subscription and the app's features once saved.
    const addons = subscription?.addons || [];
    // Only what's running is listed on the page; the "Add-ons" button opens
    // every add-on the plan offers, to turn them on or off.
    const activeAddons = addons.filter(a => a.enabled || a.endsAt);
    const [showAddonsDialog, setShowAddonsDialog] = React.useState(false);

    const handleAcknowledgeUpgrade = () => {
        setNotificationDismissed(true);
        if (!user?.entityId) return;
        setAcknowledgeUpgrade(buildApiRequestConfig(routes.subscription.acknowledgeUpgrade, [
            { param: 'entityId', value: user.entityId }
        ]));
    };

    const handlePlanSelected = () => {
        setAutoRenewOverride(null);
        refreshSubscription();
    };
    // One option per distinct horizon a real tier actually offers - always
    // in sync with the live SubscriptionTiers data, so there's no "I don't
    // see 180 days" gap and no option (like a former "2 years") that no
    // plan could ever satisfy.
    const projectionOptions: DropdownOption[] = React.useMemo(() => {
        // Plus each plan's horizon with each horizon add-on it sells on top -
        // turning one on sets the company's horizon to the total.
        const addonDays = tiers.flatMap(t => Object.keys(t.addonPrices || {})
            .map(k => parseCapacityKey(k))
            .filter(c => c?.kind === 'horizon')
            .map(c => t.horizonDays + c!.amount));
        const uniqueDays = Array.from(new Set([...tiers.map(t => t.horizonDays), ...addonDays])).sort((a, b) => a - b);
        const options = uniqueDays.map(days => ({ value: daysToHorizonLabel(days), label: daysToHorizonLabel(days) }));
        // The company's own horizon is always listed - several horizon add-ons
        // can add up to a total no single plan + add-on gives.
        if (projection && !options.some(o => o.value === projection)) options.push({ value: projection, label: projection });
        return options;
    }, [tiers, projection]);

    React.useEffect(() => {
        document.title = "FineAnts | Company Settings";
        const { url, method } = buildApiRequestConfig(routes.country.fetch);
        setFetchCountries({ url, method });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        const { url, method } = buildApiRequestConfig(routes.companySettings.fetchById, [{ param: 'id', value: user?.entityId }]);
        setFetchCompany({ url, method });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchCountries;
        if (data && !isLoading) {
            setCountryOptions(data.map((country: any) => ({ value: country.id, label: country.country })));
            setCountries(data);
        }
        if (error) {
            showError("Failed to fetch countries.");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchCountries]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchCompany;
        if (data && !isLoading && currencyOptions.length > 0) {
            const companyNameValue = data.company.name;
            const incorporationNumberValue = data.settings.incorporationNumber;
            const taxNumbersValue = data.settings.taxNumber;
            const currencyValue = currencyOptions.find(cur => cur.value === data.country.currency) || null;
            const projectionValue = data.settings.projectionHorizon;
            const dateFormatValue = data.country.dateformat;
            const selectedCountryValue = data.country.id;
            const startDayOfWeekValue = data.settings.startDayOfWeek;
            const fiscalYearStartMonthValue = String(data.settings.fiscalYearStartMonth || 7);
            const visibleHistoryValue = data.settings.visibleHistory;
            const decryptionCodeValue = '';
            const selectedStateValue = data.settings.state || null;
            const timeZoneValue = data.settings.timezone || data.country.timezone;

            // Set current values
            setCompanyName(companyNameValue);
            setIncorporationNumber(incorporationNumberValue);
            setTaxNumbers(taxNumbersValue);
            setCurrency(currencyValue);
            setProjection(projectionValue);
            setDateFormat(dateFormatValue);
            setTimeZone(timeZoneValue);
            setSelectedCountry(String(selectedCountryValue));
            setStartDayOfWeek(startDayOfWeekValue);
            setFiscalYearStartMonth(fiscalYearStartMonthValue);
            setVisibleHistory(visibleHistoryValue);
            setDecryptionCode(decryptionCodeValue);
            setSelectedState(selectedStateValue);
            setNextInvoiceNumber(data.settings.internalInvoiceNumber || '0000001');

            // Store original values per section
            setOriginalCompanyDetails({
                companyName: companyNameValue,
                incorporationNumber: incorporationNumberValue,
                taxNumbers: taxNumbersValue,
            });
            setOriginalLocalisation({
                selectedCountry: String(selectedCountryValue),
                selectedState: selectedStateValue,
                timeZone: timeZoneValue,
                dateFormat: dateFormatValue,
                currency: currencyValue,
                startDayOfWeek: startDayOfWeekValue,
                fiscalYearStartMonth: fiscalYearStartMonthValue,
            });
            setOriginalSecurity({
                decryptionCode: decryptionCodeValue,
            });
            setOriginalInternalDocuments({
                nextInvoiceNumber: data.settings.internalInvoiceNumber || '0000001',
            });
            setOriginalForecasting({
                projection: projectionValue,
                visibleHistory: visibleHistoryValue,
                consolidateOneTimeThreshold: data.settings.consolidateOneTimeThreshold || '',
            });
            setConsolidateOneTimeThreshold(data.settings.consolidateOneTimeThreshold || '');
        }
        if (error) {
            showError("Failed to fetch company settings.");
        }
    }, [fetchCompany, currencyOptions, showError]);

    // Check if any values have changed from original - per section
    const hasChangesCompanyDetails = React.useMemo(() => {
        if (!originalCompanyDetails) return false;
        return (
            companyName !== originalCompanyDetails.companyName ||
            incorporationNumber !== originalCompanyDetails.incorporationNumber ||
            taxNumbers !== originalCompanyDetails.taxNumbers
        );
    }, [originalCompanyDetails, companyName, incorporationNumber, taxNumbers]);

    const hasChangesLocalisation = React.useMemo(() => {
        if (!originalLocalisation) return false;
        return (
            selectedCountry !== originalLocalisation.selectedCountry ||
            selectedState !== originalLocalisation.selectedState ||
            timeZone !== originalLocalisation.timeZone ||
            dateFormat !== originalLocalisation.dateFormat ||
            currency?.value !== originalLocalisation.currency?.value ||
            startDayOfWeek !== originalLocalisation.startDayOfWeek ||
            fiscalYearStartMonth !== originalLocalisation.fiscalYearStartMonth
        );
    }, [originalLocalisation, selectedCountry, selectedState, timeZone, dateFormat, currency, startDayOfWeek, fiscalYearStartMonth]);

    const hasChangesSecurity = React.useMemo(() => {
        if (!originalSecurity) return false;
        return decryptionCode !== originalSecurity.decryptionCode;
    }, [originalSecurity, decryptionCode]);

    const hasChangesInternalDocuments = React.useMemo(() => {
        if (!originalInternalDocuments) return false;
        return nextInvoiceNumber !== originalInternalDocuments.nextInvoiceNumber;
    }, [originalInternalDocuments, nextInvoiceNumber]);

    const hasChangesForecasting = React.useMemo(() => {
        if (!originalForecasting) return false;
        return (
            projection !== originalForecasting.projection ||
            visibleHistory !== originalForecasting.visibleHistory ||
            consolidateOneTimeThreshold !== originalForecasting.consolidateOneTimeThreshold
        );
    }, [originalForecasting, projection, visibleHistory, consolidateOneTimeThreshold]);

    React.useEffect(() => {
        const { data, isLoading, error, errorData } = updateCompany;
        if (data && !isLoading) {
            // horizonClampedTo is only set when the request exceeded even
            // the TOP tier's limit (requireHorizon - subscription-guard-
            // service.js) - nothing to upgrade to that would satisfy it, so
            // the backend clamps rather than blocking, but "saved" still
            // has to say what actually got saved, not pretend it matched.
            if (data.horizonClampedTo) {
                showSuccess(`Saved - capped at ${data.horizonClampedTo}, the maximum even on our highest plan.`);
            } else {
                showSuccess("Company settings updated successfully.");
            }
            // Refresh company data to reset all original values
            const { url, method } = buildApiRequestConfig(routes.companySettings.fetchById, [{ param: 'id', value: user?.entityId }]);
            setFetchCompany({ url, method });
        }
        if (error) {
            // Only the Forecasting section's save (handleUpdateForecasting)
            // can ever hit this - it's the only one that sends
            // projectionHorizon, and requireHorizon's block
            // (entity-settings-controller.js) is what returns this shape.
            // A plan gap opens the app-wide upgrade prompt
            // (components/plan-required-gate.tsx) - nothing more to show here.
            if (errorData?.action === 'upgrade_required' || errorData?.action === 'addon_required') {
                // handled app-wide
            } else {
                showError(typeof error === 'string' ? error : "Failed to update company settings.");
            }
        }
    }, [updateCompany, showError, showSuccess, user?.entityId]);

    const validateForm = (): boolean => {
        const newErrors: { companyName?: string; selectedCountry?: string; timeZone?: string; selectedState?: string } = {};
        if (!companyName) {
            newErrors.companyName = "Company name is required.";
        }
        if (!selectedCountry) {
            newErrors.selectedCountry = "Country is required.";
        }
        if (!timeZone) {
            newErrors.timeZone = "Time zone is required.";
        }
        const selectedCountryName = countries.find(c => c.id === Number(selectedCountry))?.country;
        if (selectedCountryName === 'United States of America' || selectedCountryName === 'United States' || selectedCountryName === 'Switzerland') {
            if (!selectedState) {
                newErrors.selectedState = selectedCountryName === 'Switzerland' ? "Canton is required." : "State is required.";
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleUpdateCompanyDetails = () => {
        const { url, method } = buildApiRequestConfig(routes.companySettings.update, [{ param: 'id', value: user?.entityId }]);
        setUpdateCompany({ url, method, body: {
            companyName,
            incorporationNumber,
            taxNumber: taxNumbers
        } });
    }

    const handleUpdateLocalisation = () => {
        if (!selectedCountry) {
            showError("Country is required.");
            return;
        }
        if (!timeZone) {
            showError("Time zone is required.");
            return;
        }
        const selectedCountryName = countries.find(c => c.id === Number(selectedCountry))?.country;
        if (selectedCountryName === 'United States of America' || selectedCountryName === 'United States' || selectedCountryName === 'Switzerland') {
            if (!selectedState) {
                showError(selectedCountryName === 'Switzerland' ? "Canton is required." : "State is required.");
                return;
            }
        }

        const { url, method } = buildApiRequestConfig(routes.companySettings.update, [{ param: 'id', value: user?.entityId }]);
        const shouldIncludeState = selectedCountryName === 'United States of America' || selectedCountryName === 'United States' || selectedCountryName === 'Switzerland';
        
        setUpdateCompany({ url, method, body: {
            countryId: Number(selectedCountry),
            dateformat: dateFormat,
            currency: currency ? currency.value : "",
            timezone: timeZone,
            state: shouldIncludeState ? selectedState : null,
            startDayOfWeek,
            fiscalYearStartMonth: Number(fiscalYearStartMonth)
        } });
    }

    const handleUpdateSecurity = () => {
        const { url, method } = buildApiRequestConfig(routes.companySettings.update, [{ param: 'id', value: user?.entityId }]);
        setUpdateCompany({ url, method, body: {
            decryptionCode
        } });
    }

    const handleUpdateInternalDocuments = () => {
        const { url, method } = buildApiRequestConfig(routes.companySettings.update, [{ param: 'id', value: user?.entityId }]);
        setUpdateCompany({ url, method, body: {
            internalInvoiceNumber: nextInvoiceNumber
        } });
    }

    const handleUpdateForecasting = () => {
        const { url, method } = buildApiRequestConfig(routes.companySettings.update, [{ param: 'id', value: user?.entityId }]);
        setUpdateCompany({ url, method, body: {
            projectionHorizon: projection,
            visibleHistory,
            consolidateOneTimeThreshold
        } });
    }

    return (
        <div style={{ width: '100%' }}>
            <ModalWrapper
                open={openModal}
                onClose={() => setOpenModal(false)}
                title="Add Transaction">
                <></>
            </ModalWrapper>
            <Topbar title="Company"/>
            <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <SectionWrapper title="Company Details">
                    <Box display="flex" gap={2} flexWrap="wrap">
                        <Box maxWidth={300} width='100%'>
                            <Typography fontSize={14} fontWeight={500} mb={0.5}>Company Name</Typography>
                            <TextField
                                placeholder="Enter company name"
                                fullWidth
                                size="small"
                                disabled={!canUpdate}
                                error={!!errors.companyName}
                                helperText={errors.companyName}
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)} />
                        </Box>
                        <Box maxWidth={300} width='100%'>
                            <Typography fontSize={14} fontWeight={500} mb={0.5}>Incorporation Number</Typography>
                            <TextField
                                placeholder="Enter incorporation number"
                                fullWidth
                                size="small"
                                disabled={!canUpdate}
                                value={incorporationNumber}
                                onChange={(e) => {
                                    // Only allow alphanumeric characters
                                    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                                    setIncorporationNumber(value);
                                }}
                                inputProps={{
                                    pattern: '[a-zA-Z0-9]*',
                                    title: 'Only alphanumeric characters are allowed'
                                }}
                            />
                        </Box>
                        <Box maxWidth={300} width='100%'>
                            <Typography fontSize={14} fontWeight={500} mb={0.5}>Tax Number</Typography>
                            <TextField
                                placeholder="Enter tax number"
                                fullWidth
                                size="small"
                                disabled={!canUpdate}
                                value={taxNumbers}
                                onChange={(e) => {
                                    // Only allow numeric characters
                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                    setTaxNumbers(value);
                                }}
                                inputProps={{
                                    pattern: '[0-9]*',
                                    title: 'Only numeric characters are allowed'
                                }}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ color: '#fff' }}
                            onClick={handleUpdateCompanyDetails}
                            disabled={!hasChangesCompanyDetails || !canUpdate}
                        >
                            Save Changes
                        </Button>
                    </Box>
                </SectionWrapper>
                <SectionWrapper title="Localisation">
                    <Box display="flex" gap={2} flexWrap="wrap">
                        <Box maxWidth={300} width='100%'>
                            <Typography fontSize={14} fontWeight={500} mb={0.5}>Country of Operation</Typography>
                            <Autocomplete
                                options={countryOptions}
                                getOptionLabel={(option) => option.label}
                                value={countryOptions.find(opt => String(opt.value) === selectedCountry) || null}
                                disabled={!canUpdate}
                                onChange={(_, value) => {
                                    if (value) {
                                        setSelectedCountry(String(value.value));
                                        const country = countries.find(c => c.id === Number(value.value));
                                        if (country) {
                                            setTimeZone(country.timezone);
                                            setCurrency(currencyOptions.find(cur => cur.value === country.currency) || null);
                                        }
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder="Select a country"
                                        error={!!errors.selectedCountry}
                                        helperText={errors.selectedCountry}
                                    />
                                )}
                                fullWidth
                            />
                        </Box>
                        {(() => {
                            const selectedCountryName = countries.find(c => c.id === Number(selectedCountry))?.country;
                            if (selectedCountryName === 'United States of America' || selectedCountryName === 'United States') {
                                return (
                                    <Box>
                                        <Typography fontSize={14} fontWeight={500} mb={0.5}>State</Typography>
                                        <Autocomplete
                                            options={usStates.map(state => state.name)}
                                            value={selectedState}
                                            disabled={!canUpdate}
                                            onChange={(_, value) => setSelectedState(value || null)}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    placeholder="Select a state"
                                                    error={!!errors.selectedState}
                                                    helperText={errors.selectedState}
                                                />
                                            )}
                                            fullWidth
                                        />
                                    </Box>
                                );
                            } else if (selectedCountryName === 'Switzerland') {
                                return (
                                    <Box>
                                        <Typography fontSize={14} fontWeight={500} mb={0.5}>Canton</Typography>
                                        <Autocomplete
                                            options={swissCantons.map(canton => canton.name)}
                                            value={selectedState}
                                            disabled={!canUpdate}
                                            onChange={(_, value) => setSelectedState(value || null)}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    placeholder="Select a canton"
                                                    error={!!errors.selectedState}
                                                    helperText={errors.selectedState}
                                                />
                                            )}
                                            fullWidth
                                        />
                                    </Box>
                                );
                            }
                            return null;
                        })()}
                        <Box maxWidth={300} width='100%'>
                            <Typography fontSize={14} fontWeight={500} mb={0.5}>Time Zone</Typography>
                            <Autocomplete
                                options={timezones.map(tz => tz.value)}
                                value={timeZone}
                                disabled={!canUpdate}
                                onChange={(_, value) => setTimeZone(value || "")}
                                freeSolo
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder="Select or enter timezone"
                                        error={!!errors.timeZone}
                                        helperText={errors.timeZone}
                                    />
                                )}
                                fullWidth
                            />
                        </Box>
                        <Box maxWidth={300} width='100%'>
                            <Typography fontSize={14} fontWeight={500} mb={0.5}>Date Format</Typography>
                            <Dropdown
                                options={dateFormats}
                                value={dateFormat}
                                onChange={setDateFormat}
                                disabled={!canUpdate}
                                width="100%"
                                borderRadius={1}
                                height={40}
                                placeholder="Select..."
                                InputProps={{ style: { borderRadius: 4, fontSize: 16, borderColor: '#CACACA' } }}
                            />
                        </Box>
                        <Box maxWidth={300} width='100%'>
                            <Typography fontSize={14} fontWeight={500} mb={0.5}>Currency Format</Typography>
                            <Autocomplete
                                key={currency ? currency.value : 'no-currency'}
                                options={currencyOptions}
                                value={currency || undefined}
                                disabled={!canUpdate}
                                onChange={(_, v) => setCurrency(v || null)}
                                getOptionLabel={opt => opt ? opt.label : ''}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        size="small"
                                    />
                                )}
                                isOptionEqualToValue={(opt, val) => {
                                    const result = opt?.value === val?.value;
                                    return result;
                                }}
                                disableClearable
                                fullWidth
                            />
                        </Box>
                        <Box maxWidth={300} width='100%'>
                            <Typography fontSize={14} fontWeight={500} mb={0.5}>Start Day of Week</Typography>
                            <Dropdown
                                options={['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => ({ value: day.toLowerCase(), label: day }))}
                                value={startDayOfWeek}
                                onChange={setStartDayOfWeek}
                                disabled={!canUpdate}
                                width="100%"
                                borderRadius={1}
                                height={40}
                                placeholder="Select..."
                                InputProps={{ style: { borderRadius: 4, fontSize: 16, borderColor: '#CACACA' } }}
                            />
                        </Box>
                        <Box maxWidth={300} width='100%'>
                            <Typography fontSize={14} fontWeight={500} mb={0.5}>Fiscal Year Start Month</Typography>
                            <Dropdown
                                options={['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, i) => ({ value: String(i + 1), label: month }))}
                                value={fiscalYearStartMonth}
                                onChange={setFiscalYearStartMonth}
                                disabled={!canUpdate}
                                width="100%"
                                borderRadius={1}
                                height={40}
                                placeholder="Select..."
                                InputProps={{ style: { borderRadius: 4, fontSize: 16, borderColor: '#CACACA' } }}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ color: '#fff' }}
                            onClick={handleUpdateLocalisation}
                            disabled={!hasChangesLocalisation || !canUpdate}
                        >
                            Save Changes
                        </Button>
                    </Box>
                </SectionWrapper>
                <SectionWrapper title="Security">
                    <Box maxWidth={300} width='100%'>
                        <Typography fontSize={14} fontWeight={500} mb={0.5}>Decryption Code</Typography>
                        <TextField placeholder="Enter decryption code" fullWidth size="small" disabled={!canUpdate} value={decryptionCode} onChange={(e) => setDecryptionCode(e.target.value)} />
                    </Box>
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ color: '#fff' }}
                            onClick={handleUpdateSecurity}
                            disabled={!hasChangesSecurity || !canUpdate}
                        >
                            Save Changes
                        </Button>
                    </Box>
                </SectionWrapper>
                <SectionWrapper title="Internal Documents">
                    <Box display="flex" gap={4} maxWidth={600}>
                        <Box maxWidth={300} width='100%'>
                            <Typography fontSize={14} fontWeight={500} mb={0.5}>Next Invoice Number</Typography>
                            <TextField
                                placeholder="Enter next invoice number"
                                fullWidth
                                size="small"
                                disabled={!canUpdate}
                                value={nextInvoiceNumber}
                                onChange={(e) => setNextInvoiceNumber(e.target.value)}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ color: '#fff' }}
                            onClick={handleUpdateInternalDocuments}
                            disabled={!hasChangesInternalDocuments || !canUpdate}
                        >
                            Save Changes
                        </Button>
                    </Box>
                </SectionWrapper>
                <SectionWrapper title="Forecasting">
                    <Box display="flex" gap={2} flexWrap="wrap">
                        <Box maxWidth={300} width='100%'>
                            <Typography fontSize={14} fontWeight={500} mb={0.5}>Projection Horizon</Typography>
                            <Dropdown
                                options={projectionOptions}
                                value={projection}
                                onChange={setProjection}
                                disabled={!canUpdate}
                                width="100%"
                                borderRadius={1}
                                height={40}
                                placeholder="Select..."
                                InputProps={{ style: { borderRadius: 4, fontSize: 16, borderColor: '#CACACA' } }}
                            />
                        </Box>
                        {/*<Box maxWidth={300} width='100%'>
                            <Typography fontSize={14} fontWeight={500} mb={0.5}>Default Historical Transaction</Typography>
                            <UnitSelect
                                units={[
                                    { value: 'days', label: 'Days' },
                                    { value: 'weeks', label: 'Weeks' },
                                    { value: 'months', label: 'Months' },
                                    { value: 'years', label: 'Years' }
                                ]}
                                value={visibleHistory}
                                onChange={setVisibleHistory}
                                disabled={!canUpdate}
                                sx={{ width: '100%', borderRadius: 1, height: 40, fontSize: 16 }}
                            />
                        </Box>*/}
                        <Box display="flex" gap={4} maxWidth={600} flexWrap="wrap">
                            <Box>
                                <Typography fontSize={14} fontWeight={500} mb={0.5}>In Simulations, Consolidate One-time Provisions When Count Exceeds</Typography>
                                <TextField
                                    placeholder="Enter amount"
                                    fullWidth
                                    size="small"
                                    disabled={!canUpdate}
                                    value={consolidateOneTimeThreshold}
                                    onChange={(e) => setConsolidateOneTimeThreshold(e.target.value)}
                                />
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ color: '#fff' }}
                            onClick={handleUpdateForecasting}
                            disabled={!hasChangesForecasting || !canUpdate}
                        >
                            Save Changes
                        </Button>
                    </Box>
                </SectionWrapper>
                {/* Every member sees the company's subscription; only admins
                    can change it (Upgrade, Auto-Renew) - the routes that
                    change or pay for the plan (routes/subscription.js) are
                    admin-only server-side too. */}
                <Box ref={subscriptionSectionRef}>
                    <SectionWrapper title="Subscription">
                        {notification && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.5,
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(var(--color-primary-rgb), 0.08)',
                                    p: 2,
                                    mb: 3,
                                    maxWidth: 700,
                                }}
                            >
                                <Sparkles size={18} color="var(--color-primary)" style={{ flexShrink: 0, marginTop: 2 }} />
                                <Box flex={1}>
                                    <Typography fontSize={14} fontWeight={600}>
                                        You've been moved to the {upgradedToTier?.name ?? notification.toTierId} plan
                                    </Typography>
                                    <Typography fontSize={13} color="var(--color-text-muted)">
                                        One of the things you tried to use ({REASON_LABELS[notification.reason] || notification.reason}) isn't included in your previous plan, so we upgraded you automatically.
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    onClick={handleAcknowledgeUpgrade}
                                    sx={{ textTransform: 'none', fontWeight: 600, color: 'var(--color-primary)', flexShrink: 0 }}
                                >
                                    Got it
                                </Button>
                            </Box>
                        )}

                        <Box display="flex" flexDirection="column" gap={3} maxWidth={1000}>
                            <Box display="grid" gridTemplateColumns={{ xs: '1fr 1fr', sm: '1fr 1fr 1fr' }} gap={4}>
                                <DetailField
                                    label="Current Plan"
                                    value={currentTier ? (
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <span>{currentTier.name}</span>
                                            <Chip
                                                size="small"
                                                label={subscription?.status === 'expired' ? 'Trial Expired' : 'Active'}
                                                sx={{
                                                    height: 20,
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    color: '#fff',
                                                    backgroundColor: subscription?.status === 'expired' ? 'var(--color-negative-amount)' : '#1a7f37',
                                                }}
                                            />
                                        </Box>
                                    ) : undefined}
                                />
                                <DetailField
                                    label="Started On"
                                    value={subscription?.startedAt ? formatDate(subscription.startedAt) : undefined}
                                />
                                {!currentTier?.billing ? (
                                    <DetailField
                                        label="Trial Ends On"
                                        value={subscription?.trialEndsOn ? formatDate(subscription.trialEndsOn) : undefined}
                                    />
                                ) : (
                                    <DetailField
                                        label={autoRenew ? 'Renews On' : 'Access Ends On'}
                                        value={subscription?.renewsOn ? formatDate(subscription.renewsOn) : undefined}
                                    />
                                )}
                                {currentTier?.billing && (
                                    <DetailField
                                        label="Renewal Amount"
                                        value={`${formatMoney(currentTier.minimumGuaranteed)} / ${currentTier.billing === 'annual' ? 'year' : 'month'}`}
                                    />
                                )}
                                {currentTier?.billing && (
                                    <Box>
                                        <Typography fontWeight={500} fontSize={14} mb={0.5} color="var(--color-text-muted)">Auto-Renew</Typography>
                                        {/* Read-only "On"/"Off" for non-admins - only
                                            admins can change it. */}
                                        {isAdmin
                                            ? <Toggle checked={autoRenew} onChange={handleToggleAutoRenew} size="small" disabled={!canUpdate} />
                                            : <Typography fontSize={15}>{autoRenew ? 'On' : 'Off'}</Typography>}
                                    </Box>
                                )}
                                {addons.length > 0 && (
                                    <Box>
                                        <Typography fontWeight={500} fontSize={14} mb={0.5} color="var(--color-text-muted)">Add-ons</Typography>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => setShowAddonsDialog(true)}
                                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}
                                        >
                                            {isAdmin ? 'Manage add-ons' : 'View add-ons'} ({activeAddons.length}/{addons.length})
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                            {overdue && (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, maxWidth: 700, px: 2, py: 1.5, borderRadius: 2, border: '1px solid #e53935' }}>
                                    <Box>
                                        <Typography fontWeight={600} fontSize={14} color="#e53935">Payment overdue</Typography>
                                        <Typography fontSize={13}>{formatMoney(overdue.amount ?? 0)}{overdue.planName ? ` - ${overdue.planName} plan` : ''}</Typography>
                                    </Box>
                                    {isAdmin && (
                                        <Button
                                            variant="contained"
                                            onClick={() => setShowOverduePay(true)}
                                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, boxShadow: 'none', background: 'var(--color-primary)' }}
                                        >
                                            Pay now
                                        </Button>
                                    )}
                                </Box>
                            )}
                            {/* The add-ons that are running (on, or off but still paid
                                up) - every add-on the plan offers is in the
                                "Add-ons" dialog. */}
                            {activeAddons.length > 0 && (
                                <Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 2, maxWidth: 700 }}>
                                    <Typography fontWeight={600} fontSize={14} sx={{ px: 2, pt: 1.5 }}>Add-ons</Typography>
                                    {activeAddons.map((addon, index) => {
                                        const description = addonDescription(addon.key);
                                        const price = addon.price != null && addon.price > 0
                                            ? ` - ${formatMoney(addon.price)} / month${addon.billing === 'annual' ? `, billed ${formatMoney(addon.price * 12)} yearly` : ''}`
                                            : ' - free';
                                        return (
                                            <Box key={addon.key} sx={{ px: 2, py: 1.5, borderTop: index > 0 ? '1px solid rgba(0,0,0,0.08)' : 'none' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                                    <Box>
                                                        <Typography fontWeight={600} fontSize={13.5}>{addon.label}</Typography>
                                                        <Typography fontSize={12.5} color="var(--color-text-muted)">
                                                            {description}{price}.
                                                        </Typography>
                                                    </Box>
                                                    <Typography fontSize={15}>{addon.enabled ? 'On' : 'Ending'}</Typography>
                                                </Box>
                                                <Typography fontSize={12} color="var(--color-text-muted)" sx={{ mt: 0.5 }}>
                                                    {addon.enabled
                                                        ? `On - renews with your plan${addon.billedFrom ? ` on ${formatDate(addon.billedFrom)}` : ''}.`
                                                        : `Turned off - ${addon.label} stays available until ${formatDate(addon.endsAt as string)}${addon.key === 'documents' ? ', then shared links stop working' : ''}.`}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                            {currentTier?.billing && !autoRenew && (
                                <Typography fontSize={12.5} color="var(--color-text-muted)">
                                    Auto-renew is off - your plan won't charge again and access ends on {subscription?.renewsOn ? formatDate(subscription.renewsOn) : 'the date above'}. {isAdmin ? 'Turn it back on anytime before then to keep your plan.' : 'A company admin can turn it back on before then to keep the plan.'}
                                </Typography>
                            )}
                            {!isAdmin && (
                                <Typography fontSize={12.5} color="var(--color-text-muted)">
                                    Only company admins can change the plan.
                                </Typography>
                            )}
                            {/* "Manage" on every plan - any other plan can be picked,
                                up or down (moving a paying plan to Free waits for the
                                end of the paid period). An expired trial gets
                                "Choose a Plan". Admins only. */}
                            {isAdmin && (
                                <Box>
                                    <Button
                                        variant="contained"
                                        onClick={() => setShowPlans(true)}
                                        disabled={!canUpdate}
                                        sx={{ bgcolor: 'var(--color-primary)', color: '#fff', borderRadius: 2, px: 4, fontWeight: 600, textTransform: 'none' }}
                                    >
                                        {isExpired ? 'Choose a Plan' : 'Manage'}
                                    </Button>
                                </Box>
                            )}

                            <Divider sx={{ my: 1 }} />

                            <Box>
                                <Typography fontWeight={600} fontSize={15} mb={1.5}>Billing History</Typography>
                                {billingHistory.length === 0 ? (
                                    <Typography fontSize={13.5} color="var(--color-text-muted)">No subscription payments or refunds yet.</Typography>
                                ) : (
                                    <Box sx={{ overflowX: 'auto' }}>
                                        <Box component="table" sx={{ width: '100%', maxWidth: 800, borderCollapse: 'collapse', fontSize: 13.5 }}>
                                            <Box component="thead">
                                                <Box component="tr">
                                                    <Box component="th" sx={{ textAlign: 'left', py: 1, color: 'var(--color-text-muted)', fontWeight: 500, borderBottom: '1px solid var(--color-border, #e0e0e0)' }}>Date</Box>
                                                    <Box component="th" sx={{ textAlign: 'left', py: 1, color: 'var(--color-text-muted)', fontWeight: 500, borderBottom: '1px solid var(--color-border, #e0e0e0)' }}>Description</Box>
                                                    <Box component="th" sx={{ textAlign: 'left', py: 1, color: 'var(--color-text-muted)', fontWeight: 500, borderBottom: '1px solid var(--color-border, #e0e0e0)' }}>Type</Box>
                                                    <Box component="th" sx={{ textAlign: 'right', py: 1, color: 'var(--color-text-muted)', fontWeight: 500, borderBottom: '1px solid var(--color-border, #e0e0e0)' }}>Amount</Box>
                                                </Box>
                                            </Box>
                                            <Box component="tbody">
                                                {billingHistory.map(row => (
                                                    <Box component="tr" key={row.guid}>
                                                        <Box component="td" sx={{ py: 1, borderBottom: '1px solid var(--color-border, #f0f0f0)' }}>{formatDate(row.date)}</Box>
                                                        <Box component="td" sx={{ py: 1, borderBottom: '1px solid var(--color-border, #f0f0f0)' }}>{row.description}</Box>
                                                        <Box component="td" sx={{ py: 1, borderBottom: '1px solid var(--color-border, #f0f0f0)' }}>
                                                            <Chip
                                                                size="small"
                                                                label={row.type === 'refund' ? 'Refund' : 'Payment'}
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize: 11,
                                                                    fontWeight: 700,
                                                                    color: '#fff',
                                                                    backgroundColor: row.type === 'refund' ? '#1a7f37' : 'var(--color-text-muted)',
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box
                                                            component="td"
                                                            sx={{
                                                                py: 1,
                                                                textAlign: 'right',
                                                                borderBottom: '1px solid var(--color-border, #f0f0f0)',
                                                                color: row.amount < 0 ? 'var(--color-negative-amount)' : '#1a7f37',
                                                                fontVariantNumeric: 'tabular-nums',
                                                            }}
                                                        >
                                                            {formatMoneyCents(row.amount)}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </SectionWrapper>
                </Box>
            </div>

            {/* Add-ons - the same checkout page a plan is bought on. */}
            <OverduePaymentCheckout
                open={showOverduePay}
                onClose={() => setShowOverduePay(false)}
                overdue={overdue}
                onPaid={() => { refreshSubscription(); reloadFeatures(); }}
            />
            <AddonsCheckout
                open={showAddonsDialog}
                onClose={() => setShowAddonsDialog(false)}
                plan={currentTier ?? null}
                addons={addons}
                readOnly={!isAdmin || !canUpdate}
                onDone={() => { refreshSubscription(); reloadFeatures(); }}
            />

            <SubscriptionPlans
                open={showPlans}
                onClose={() => setShowPlans(false)}
                onSelect={handlePlanSelected}
                currentTierId={isExpired ? undefined : currentTier?.id}
            />
        </div>
    );
};

export default AccountSettings;